import { useState } from "react";
import { Link } from "react-router-dom";
import { Trophy, Plus, Trash2, Fish, Info, Share2, Check } from "lucide-react";
import { BOAT, SHORE, parseLbsOz } from "./cfsa-data";
import PageMeta from "./PageMeta";

const TARGETS = [
  { value: "s",  label: "CFSA Specimen"    },
  { value: "q",  label: "CFSA Qualifying"  },
  { value: "od", label: "One Day Specimen" },
];

const COMBINED = [
  ...SHORE.map((r) => ({ ...r, _location: "shore" })),
  ...BOAT.filter((r) => !SHORE.find((s) => s.species === r.species))
         .map((r) => ({ ...r, _location: "boat" })),
].sort((a, b) => a.species.localeCompare(b.species));

function calcPct(record, weightDecLbs, targetField) {
  const targetWeight = parseLbsOz(record[targetField + "Lbs"]);
  if (!targetWeight) return 0;
  return (weightDecLbs / targetWeight) * 100;
}

let nextId = 1;
const uid = () => nextId++;

function formatWeight(weightDec) {
  const totalDrms = Math.round(weightDec * 256);
  const lbs  = Math.floor(totalDrms / 256);
  const oz   = Math.floor((totalDrms % 256) / 16);
  const drms = totalDrms % 16;
  const parts = [];
  if (lbs  > 0) parts.push(`${lbs}lbs`);
  if (oz   > 0) parts.push(`${oz}oz`);
  if (drms > 0) parts.push(`${drms}drms`);
  return parts.length > 0 ? parts.join(" ") : "0oz";
}

function FishEntry({ fish, location, target, onRemove, duplicate }) {
  const data = fish.fishLocation === "boat" ? BOAT : fish.fishLocation === "shore" ? SHORE : (location === "boat" ? BOAT : location === "shore" ? SHORE : COMBINED);
  const record = data.find((r) => r.species === fish.species);
  const pct = record && fish.weightDec > 0 ? calcPct(record, fish.weightDec, target) : null;

  return (
    <div className={`flex items-center justify-between gap-3 rounded-lg border px-4 py-2.5 text-sm ${duplicate ? "border-amber-400/40 bg-amber-400/5" : "border-white/10 bg-white/5"}`}>
      <div className="flex-1 min-w-0">
        <span className="font-semibold text-white">{fish.species}</span>
        <span className="ml-2 text-slate-400">{formatWeight(fish.weightDec)}</span>
        {fish.fishLocation && <span className="ml-2 text-xs text-slate-500 capitalize">{fish.fishLocation}</span>}
        {duplicate && <span className="ml-2 text-xs text-amber-400">duplicate species — excluded</span>}
      </div>
      {pct !== null && (
        <span className={`shrink-0 font-bold tabular-nums ${duplicate ? "text-slate-500 line-through" : pct >= 100 ? "text-cyan-300" : pct >= 75 ? "text-emerald-300" : "text-amber-300"}`}>
          {pct.toFixed(1)}%
        </span>
      )}
      <button type="button" onClick={onRemove} className="shrink-0 text-slate-500 hover:text-red-400 transition">
        <Trash2 className="h-4 w-4" />
      </button>
    </div>
  );
}

function AddFishForm({ location, target, onAdd, uniqueSpecies, existingSpecies }) {
  const [fishLoc, setFishLoc] = useState("shore");
  const effectiveLoc = location === "both" ? fishLoc : location;
  const data = effectiveLoc === "boat" ? BOAT : SHORE;
  const [species, setSpecies] = useState("");
  const [metric,  setMetric]  = useState(false);
  // imperial
  const [lbs,  setLbs]  = useState("0");
  const [oz,   setOz]   = useState("0");
  const [drms, setDrms] = useState("0");
  // metric
  const [kg,  setKg]  = useState("0");
  const [g,   setG]   = useState("0");

  const isDuplicate = uniqueSpecies && species && existingSpecies.includes(species);

  const isDisabled = !species || (metric ? (kg === "") : (lbs === ""));

  const handleAdd = () => {
    if (isDisabled) return;
    let weightDec;
    if (metric) {
      const kgNum = parseFloat(kg) || 0;
      const gNum  = parseFloat(g)  || 0;
      weightDec = (kgNum * 1000 + gNum) / 453.59237;
    } else {
      weightDec = (parseInt(lbs) || 0) + (parseInt(oz) || 0) / 16 + (parseInt(drms) || 0) / 256;
    }
    onAdd({ id: uid(), species, weightDec, fishLocation: location === "both" ? fishLoc : null });
    setSpecies("");
    setLbs("0"); setOz("0"); setDrms("0");
    setKg("0");  setG("0");
  };

  return (
    <div className="mt-3">
      <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto_auto_auto_auto] gap-2 items-end">
        <div>
          <label className="mb-1 block text-xs font-semibold uppercase tracking-widest text-slate-500">Species</label>
          <select
            value={species}
            onChange={(e) => setSpecies(e.target.value)}
            className="w-full rounded-lg border border-white/10 bg-slate-800 px-2.5 py-2 text-sm text-white outline-none focus:border-cyan-300/40"
          >
            <option value="">— Select —</option>
            {data.map((r) => <option key={r.species} value={r.species}>{r.species}</option>)}
          </select>
        </div>

        {metric ? (
          <>
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-widest text-slate-500">Kg</label>
              <input
                type="number" min="0" placeholder="0"
                value={kg}
                onChange={(e) => setKg(e.target.value)}
                className="w-full sm:w-20 rounded-lg border border-white/10 bg-slate-800 px-2.5 py-2 text-sm text-white outline-none focus:border-cyan-300/40"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-widest text-slate-500">G</label>
              <input
                type="number" min="0" max="999" placeholder="0"
                value={g}
                onChange={(e) => setG(e.target.value)}
                className="w-full sm:w-20 rounded-lg border border-white/10 bg-slate-800 px-2.5 py-2 text-sm text-white outline-none focus:border-cyan-300/40"
              />
            </div>
          </>
        ) : (
          <>
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-widest text-slate-500">Lbs</label>
              <input
                type="number" min="0" placeholder="0"
                value={lbs}
                onChange={(e) => setLbs(e.target.value)}
                className="w-full sm:w-20 rounded-lg border border-white/10 bg-slate-800 px-2.5 py-2 text-sm text-white outline-none focus:border-cyan-300/40"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-widest text-slate-500">Oz</label>
              <select
                value={oz}
                onChange={(e) => setOz(e.target.value)}
                className="w-full sm:w-20 rounded-lg border border-white/10 bg-slate-800 px-2.5 py-2 text-sm text-white outline-none focus:border-cyan-300/40"
              >
                {Array.from({ length: 16 }, (_, i) => (
                  <option key={i} value={i}>{i}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-widest text-slate-500">Drms</label>
              <select
                value={drms}
                onChange={(e) => setDrms(e.target.value)}
                className="w-full sm:w-20 rounded-lg border border-white/10 bg-slate-800 px-2.5 py-2 text-sm text-white outline-none focus:border-cyan-300/40"
              >
                {Array.from({ length: 16 }, (_, i) => (
                  <option key={i} value={i}>{i}</option>
                ))}
              </select>
            </div>
          </>
        )}

        <button
          type="button"
          onClick={handleAdd}
          disabled={isDisabled}
          className="flex items-center justify-center sm:justify-start gap-1.5 rounded-lg bg-cyan-500 px-3 py-2 text-sm font-semibold text-slate-950 transition hover:bg-cyan-400 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <Plus className="h-4 w-4" />
          Add
        </button>
      </div>

      <div className="mt-2 flex items-center gap-3 flex-wrap">
        {location === "both" && (
          <div className="flex items-center gap-1 rounded-lg border border-white/10 bg-slate-800 p-0.5 text-xs font-semibold">
            <button
              type="button"
              onClick={() => { setFishLoc("shore"); setSpecies(""); }}
              className={`rounded-md px-3 py-1 transition ${fishLoc === "shore" ? "bg-cyan-500 text-slate-950" : "text-slate-400 hover:text-white"}`}
            >
              Shore
            </button>
            <button
              type="button"
              onClick={() => { setFishLoc("boat"); setSpecies(""); }}
              className={`rounded-md px-3 py-1 transition ${fishLoc === "boat" ? "bg-cyan-500 text-slate-950" : "text-slate-400 hover:text-white"}`}
            >
              Boat
            </button>
          </div>
        )}
        <label className="flex items-center gap-2 cursor-pointer text-xs text-slate-400 select-none">
          <input
            type="checkbox"
            checked={metric}
            onChange={(e) => setMetric(e.target.checked)}
            className="h-3.5 w-3.5 rounded border-white/20 bg-slate-800 accent-cyan-500 cursor-pointer"
          />
          Enter weight in kg / g
        </label>
      </div>

      {isDuplicate && (
        <p className="mt-1.5 text-xs text-amber-400">
          This angler already has a {species} entered. With "unique species only" enabled it will be excluded from scoring.
        </p>
      )}
    </div>
  );
}

const MEDALS = [
  { label: "1st", bg: "bg-amber-400/20 border-amber-400/40", text: "text-amber-300", icon: "🥇" },
  { label: "2nd", bg: "bg-slate-400/10 border-slate-400/30", text: "text-slate-300",  icon: "🥈" },
  { label: "3rd", bg: "bg-orange-700/20 border-orange-600/30", text: "text-orange-400", icon: "🥉" },
];

export default function CompetitionCalculatorPage() {
  const [compName,      setCompName]      = useState("e.g. Easter Competition");
  const [location,      setLocation]      = useState("shore");
  const [target,        setTarget]        = useState("s");
  const [maxFish,       setMaxFish]       = useState(4);
  const [uniqueSpecies, setUniqueSpecies] = useState(false);
  const [fishCountPriority, setFishCountPriority] = useState(false);
  const [heaviestBag,       setHeaviestBag]       = useState(false);
  const [anglers,       setAnglers]       = useState([]);
  const [newName,       setNewName]       = useState("");
  const [showHelp,      setShowHelp]      = useState(false);
  const [showSnapshot,  setShowSnapshot]  = useState(false);
  const [showFishInResults, setShowFishInResults] = useState(true);
  const [copied, setCopied] = useState(false);

  const addAngler = () => {
    const name = newName.trim();
    if (!name) return;
    setAnglers((prev) => [...prev, { id: uid(), name, fish: [] }]);
    setNewName("");
  };

  const removeAngler = (anglerId) =>
    setAnglers((prev) => prev.filter((a) => a.id !== anglerId));

  const addFish = (anglerId, fish) =>
    setAnglers((prev) =>
      prev.map((a) => a.id === anglerId ? { ...a, fish: [...a.fish, fish] } : a)
    );

  const removeFish = (anglerId, fishId) =>
    setAnglers((prev) =>
      prev.map((a) => a.id === anglerId ? { ...a, fish: a.fish.filter((f) => f.id !== fishId) } : a)
    );

  const data = location === "boat" ? BOAT : location === "shore" ? SHORE : COMBINED;

  const ranked = anglers
    .map((angler) => {
      const scored = angler.fish.map((f) => {
        const fishData = f.fishLocation === "boat" ? BOAT : f.fishLocation === "shore" ? SHORE : data;
        const record = fishData.find((r) => r.species === f.species);
        const pct   = heaviestBag ? f.weightDec : (record && f.weightDec > 0 ? calcPct(record, f.weightDec, target) : 0);
        return { ...f, pct };
      });

      let eligible = scored;
      if (uniqueSpecies) {
        const seen = new Map();
        scored.forEach((f) => {
          if (!seen.has(f.species) || f.pct > seen.get(f.species).pct) {
            seen.set(f.species, f);
          }
        });
        eligible = scored.map((f) => ({
          ...f,
          excluded: seen.get(f.species)?.id !== f.id,
        }));
      } else {
        eligible = scored.map((f) => ({ ...f, excluded: false }));
      }

      const sorted   = [...eligible].filter((f) => !f.excluded).sort((a, b) => b.pct - a.pct);
      const counting = heaviestBag ? sorted : sorted.slice(0, maxFish);
      const total    = counting.reduce((sum, f) => sum + f.pct, 0);
      return { ...angler, eligible, counting, total };
    })
    .sort((a, b) => {
      if (fishCountPriority) {
        const countDiff = b.counting.length - a.counting.length;
        if (countDiff !== 0) return countDiff;
      }
      return b.total - a.total;
    });

  const hasResults = ranked.some((a) => a.total > 0);

  const buildShareText = () => {
    const targetLabel = TARGETS.find((t) => t.value === target)?.label;
    const lines = [
      `🏆 ${compName}`,
      `${targetLabel} · Best ${maxFish} fish${uniqueSpecies ? " · unique species" : ""}${fishCountPriority ? " · fish count priority" : ""}`,
      "",
    ];
    ranked.filter((a) => a.total > 0).forEach((angler, idx) => {
      const medal = ["🥇", "🥈", "🥉"][idx] ?? `${idx + 1}.`;
      lines.push(`${medal} ${angler.name} — ${heaviestBag ? formatWeight(angler.total) : `${angler.total.toFixed(1)}%`}`);
      if (showFishInResults) {
        angler.counting.forEach((f) => {
          const weight = formatWeight(f.weightDec);
          lines.push(`   ${f.species} ${weight}${heaviestBag ? "" : ` (${f.pct.toFixed(1)}%)`}`);
        });
      }
    });
    return lines.join("\n");
  };

  const handleShare = async () => {
    const text = buildShareText();
    if (navigator.share) {
      try {
        await navigator.share({ title: compName, text });
        return;
      } catch (_) {}
    }
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="page-shell min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 text-white">
      <PageMeta
        title="Competitions Leaderboard Calculator | Redruth Sea Angling Association"
        description="Calculate and rank sea angling competition leaderboards for RSAA club matches in Cornwall — enter catches and see standings instantly."
        keywords="sea angling competition calculator, RSAA leaderboard, fishing competition scoring Cornwall"
        url="https://www.redruthseaanglingassociation.co.uk/competition-calculator"
      />
      <div className="mx-auto max-w-4xl px-4 py-12 sm:px-6 lg:px-8">

        <Link
          to="/"
          className="standalone-only inline-flex items-center gap-2 rounded-lg border border-cyan-300/40 bg-cyan-400/10 px-4 py-2 text-sm text-cyan-200 transition hover:bg-cyan-400/20"
        >
          ← Back
        </Link>

        <div className="mt-8 flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-4">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-violet-400/10">
              <Trophy className="h-6 w-6 text-violet-300" />
            </div>
            <div>
              <p className="text-sm font-semibold uppercase tracking-[0.3em] text-violet-300">RSAA</p>
              <h1 className="heading-glow mt-1 text-3xl font-bold tracking-tight text-white sm:text-4xl">Competitions Leaderboard Calculator</h1>
            </div>
          </div>
          <button
            type="button"
            onClick={() => setShowHelp((v) => !v)}
            className="inline-flex items-center gap-2 rounded-lg border border-violet-400/30 bg-violet-400/10 px-4 py-2 text-sm text-violet-200 transition hover:bg-violet-400/20"
          >
            <Info className="h-4 w-4" />
            {showHelp ? "Hide instructions" : "How to use"}
          </button>
        </div>

        {/* Instructions panel */}
        {showHelp && (
          <div className="mt-5 rounded-2xl border border-violet-400/20 bg-violet-400/5 p-6 text-sm leading-7 text-slate-300 space-y-3 panel-lift">
            <h2 className="text-base font-semibold text-white">How to use the Competitions Leaderboard Calculator</h2>
            <ol className="list-decimal list-inside space-y-2 text-slate-300">
              <li>
                <strong className="text-white">Set up the competition</strong> — Enter a name (e.g. <em>Easter Competition</em>), choose Shore, Boat, or both, select your scoring target (usually <em>CFSA Specimen</em>), and set how many fish count per angler (e.g. <em>4</em> for "best 4 fish").
              </li>
              <li>
                <strong className="text-white">Unique species rule</strong> — Tick "Each fish must be a different species" if the competition rules only allow one fish per species per angler. The calculator will automatically keep only the best-scoring fish of each species.
              </li>
              <li>
                <strong className="text-white">Number of fish priority</strong> — Tick this if the competition rules rank an angler with more counting fish above one with fewer, even if their total % is lower. For example, an angler with 4 fish at 345% would beat one with 3 fish at 367%. Percentage is used as a tiebreaker when fish counts are equal.
              </li>
              <li>
                <strong className="text-white">Add anglers</strong> — Type each angler's name and click <em>Add Angler</em>. Press Enter as a shortcut.
              </li>
              <li>
                <strong className="text-white">Enter fish</strong> — For each angler, select the species, enter the weight, and click <em>Add</em>. By default weights are entered in <strong className="text-white">lbs / oz / drms</strong> — tick <em>"Enter weight in kg / g"</em> to switch to metric. All weights are stored and displayed in lbs, oz, and drms regardless of how they were entered. The CFSA percentage is shown instantly next to each fish.
              </li>
              <li>
                <strong className="text-white">Heaviest bag</strong> — Tick this to score by total weight instead of CFSA percentage. Anglers are ranked by the combined weight of their best N fish. The scoring target setting is not used in this mode.
              </li>
              <li>
                <strong className="text-white">Results</strong> — The leaderboard updates live. Each angler's best N fish are totalled (by % or by weight in heaviest bag mode) and ranked automatically, with 🥇🥈🥉 for the top three.
              </li>
              <li>
                <strong className="text-white">Show / Hide fish</strong> — Use the <em>Show fish</em> / <em>Hide fish</em> toggle in the results header to switch between showing a full breakdown of each angler's counting fish (species, weight, and %) and a clean totals-only view.
              </li>
              <li>
                <strong className="text-white">Screenshot leaderboard</strong> — Click <em>📸 Screenshot Leaderboard</em> to open a clean panel ready to screenshot and share. If "Show fish" is active the panel will include each angler's individual fish; if hidden it shows totals only.
              </li>
            </ol>
            <p className="text-xs text-slate-500">All percentages are calculated against the CFSA published specimen weights (2023–2025).</p>
          </div>
        )}

        {/* Competition settings */}
        <div className="mt-6 rounded-2xl border border-white/10 bg-slate-900/80 p-6 panel-lift">
          <h2 className="text-base font-semibold text-white mb-4">Competition Settings</h2>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <div className="sm:col-span-2 lg:col-span-1">
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-widest text-slate-400">Competition Name</label>
              <input
                type="text"
                value={compName}
                onChange={(e) => setCompName(e.target.value)}
                placeholder="e.g. Easter Competition"
                className="w-full rounded-xl border border-white/10 bg-slate-800 px-3 py-2.5 text-sm text-white outline-none focus:border-violet-300/40 placeholder:text-slate-600"
              />
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-widest text-slate-400">Fishing Type</label>
              <select
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                className="w-full rounded-xl border border-white/10 bg-slate-800 px-3 py-2.5 text-sm text-white outline-none focus:border-violet-300/40"
              >
                <option value="shore">Shore</option>
                <option value="boat">Boat</option>
                <option value="both">Shore &amp; Boat</option>
              </select>
            </div>
            <div className={heaviestBag ? "opacity-40 pointer-events-none" : ""}>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-widest text-slate-400">Scoring Target</label>
              <select
                value={target}
                onChange={(e) => setTarget(e.target.value)}
                className="w-full rounded-xl border border-white/10 bg-slate-800 px-3 py-2.5 text-sm text-white outline-none focus:border-violet-300/40"
              >
                {TARGETS.map((t) => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </div>
            <div className={heaviestBag ? "opacity-40 pointer-events-none" : ""}>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-widest text-slate-400">Best N Fish to Count</label>
              <select
                value={maxFish}
                onChange={(e) => setMaxFish(parseInt(e.target.value))}
                className="w-full rounded-xl border border-white/10 bg-slate-800 px-3 py-2.5 text-sm text-white outline-none focus:border-violet-300/40"
              >
                {Array.from({ length: 20 }, (_, i) => (
                  <option key={i + 1} value={i + 1}>{i + 1}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Unique species checkbox */}
          <label className="mt-4 flex cursor-pointer items-start gap-3">
            <input
              type="checkbox"
              checked={uniqueSpecies}
              onChange={(e) => setUniqueSpecies(e.target.checked)}
              className="mt-0.5 h-4 w-4 rounded border-white/20 bg-slate-800 accent-violet-500 cursor-pointer"
            />
            <div>
              <span className="text-sm font-semibold text-white">Each fish must be a different species</span>
              <p className="text-xs text-slate-500 mt-0.5">If an angler enters multiple fish of the same species, only the best-scoring one will count.</p>
            </div>
          </label>

          {/* Fish count priority checkbox */}
          <label className={`mt-3 flex cursor-pointer items-start gap-3 ${heaviestBag ? "opacity-40 pointer-events-none" : ""}`}>
            <input
              type="checkbox"
              checked={fishCountPriority}
              onChange={(e) => setFishCountPriority(e.target.checked)}
              className="mt-0.5 h-4 w-4 rounded border-white/20 bg-slate-800 accent-violet-500 cursor-pointer"
            />
            <div>
              <span className="text-sm font-semibold text-white">Number of fish priority</span>
              <p className="text-xs text-slate-500 mt-0.5">An angler with more counting fish ranks above one with fewer, regardless of total %. Percentage is used as a tiebreaker only.</p>
            </div>
          </label>

          {/* Heaviest bag checkbox */}
          <label className="mt-3 flex cursor-pointer items-start gap-3">
            <input
              type="checkbox"
              checked={heaviestBag}
              onChange={(e) => setHeaviestBag(e.target.checked)}
              className="mt-0.5 h-4 w-4 rounded border-white/20 bg-slate-800 accent-violet-500 cursor-pointer"
            />
            <div>
              <span className="text-sm font-semibold text-white">Heaviest bag</span>
              <p className="text-xs text-slate-500 mt-0.5">Rank anglers by total weight of their best N fish instead of CFSA percentage. Scoring target is not used in this mode.</p>
            </div>
          </label>

          <p className="mt-3 text-xs text-slate-500">
            Each angler's best <strong className="text-slate-300">{maxFish}</strong> fish {heaviestBag ? "by weight" : `(by % of ${TARGETS.find(t => t.value === target)?.label} weight)`} will count toward their total score.
          </p>
        </div>

        {/* Add anglers */}
        <div className="mt-6 rounded-2xl border border-white/10 bg-slate-900/80 p-6 panel-lift">
          <h2 className="text-base font-semibold text-white mb-4">Anglers &amp; Fish</h2>

          <div className="flex gap-2 flex-col sm:flex-row">
            <input
              type="text"
              placeholder="Angler name…"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && addAngler()}
              className="flex-1 rounded-xl border border-white/10 bg-slate-800 px-3 py-2.5 text-sm text-white outline-none focus:border-violet-300/40 placeholder:text-slate-500"
            />
            <button
              type="button"
              onClick={addAngler}
              disabled={!newName.trim()}
              className="flex items-center justify-center gap-2 rounded-xl bg-violet-500 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-violet-400 disabled:opacity-40 disabled:cursor-not-allowed shrink-0 sm:justify-start"
            >
              <Plus className="h-4 w-4" />
              Add Angler
            </button>
          </div>

          {anglers.length === 0 && (
            <p className="mt-6 text-center text-sm text-slate-500">No anglers added yet. Enter a name above and click <em>Add Angler</em>.</p>
          )}

          <div className="mt-4 space-y-4">
            {anglers.map((angler) => {
              const existingSpecies = angler.fish.map((f) => f.species);
              return (
                <div key={angler.id} className="rounded-xl border border-white/10 bg-white/5 p-4 panel-lift">
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <Fish className="h-4 w-4 text-violet-400" />
                      <span className="font-semibold text-white">{angler.name}</span>
                      <span className="text-xs text-slate-500">{angler.fish.length} fish entered</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeAngler(angler.id)}
                      className="text-slate-500 hover:text-red-400 transition"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>

                  <div className="mt-3 space-y-2">
                    {[...angler.fish]
                      .sort((a, b) => {
                        const rankedAngler = ranked.find((r) => r.id === angler.id);
                        const pctA = rankedAngler?.eligible?.find((e) => e.id === a.id)?.pct ?? 0;
                        const pctB = rankedAngler?.eligible?.find((e) => e.id === b.id)?.pct ?? 0;
                        return pctB - pctA;
                      })
                      .map((f) => {
                      const scoredFish = ranked.find((a) => a.id === angler.id)?.eligible?.find((e) => e.id === f.id);
                      return (
                        <FishEntry
                          key={f.id}
                          fish={f}
                          location={location}
                          target={target}
                          duplicate={scoredFish?.excluded ?? false}
                          onRemove={() => removeFish(angler.id, f.id)}
                        />
                      );
                    })}
                  </div>

                  <AddFishForm
                    location={location}
                    target={target}
                    uniqueSpecies={uniqueSpecies}
                    existingSpecies={existingSpecies}
                    onAdd={(fish) => addFish(angler.id, fish)}
                  />
                </div>
              );
            })}
          </div>
        </div>

        {/* Results leaderboard */}
        {ranked.length > 0 && (
          <div className="mt-6 rounded-2xl border border-white/10 bg-slate-900/80 p-6 panel-lift">
            <div className="flex items-start justify-between gap-3 flex-wrap mb-1">
              <h2 className="text-base font-semibold text-white">{compName} — Results</h2>
              {hasResults && (
                <div className="flex items-center gap-2 flex-wrap">
                  <button
                    type="button"
                    onClick={() => setShowFishInResults((v) => !v)}
                    className="inline-flex items-center gap-2 rounded-lg border border-violet-400/30 bg-violet-400/10 px-3 py-1.5 text-xs font-semibold text-violet-200 transition hover:bg-violet-400/20"
                  >
                    <Fish className="h-3.5 w-3.5" />
                    {showFishInResults ? "Hide fish" : "Show fish"}
                  </button>
                  <button
                    type="button"
                    onClick={handleShare}
                    className="inline-flex items-center gap-2 rounded-lg border border-violet-400/30 bg-violet-400/10 px-3 py-1.5 text-xs font-semibold text-violet-200 transition hover:bg-violet-400/20"
                  >
                    {copied ? <Check className="h-3.5 w-3.5 text-emerald-400" /> : <Share2 className="h-3.5 w-3.5" />}
                    {copied ? "Copied!" : "Share"}
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowSnapshot(true)}
                    className="inline-flex items-center gap-2 rounded-lg border border-violet-400/30 bg-violet-400/10 px-3 py-1.5 text-xs font-semibold text-violet-200 transition hover:bg-violet-400/20"
                  >
                    📸 Screenshot Leaderboard
                  </button>
                </div>
              )}
            </div>
            <p className="text-xs text-slate-500 mb-5">
              {heaviestBag ? "All fish" : `Best ${maxFish} fish per angler`}{uniqueSpecies ? ", one per species" : ""}{!heaviestBag && fishCountPriority ? ", fish count priority" : ""} · {heaviestBag ? "Heaviest bag" : TARGETS.find(t => t.value === target)?.label} · {location === "both" ? "Shore & Boat" : location.charAt(0).toUpperCase() + location.slice(1)}
            </p>

            <div className="space-y-3">
              {ranked.map((angler, idx) => {
                const medal = MEDALS[idx];
                return (
                  <div
                    key={angler.id}
                    className={`rounded-xl border p-4 ${medal ? medal.bg : "border-white/10 bg-white/5"}`}
                  >
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                      <div className="flex items-center gap-3">
                        <span className="text-2xl">{medal ? medal.icon : `${idx + 1}.`}</span>
                        <div>
                          <p className={`font-bold text-lg ${medal ? medal.text : "text-slate-300"}`}>{angler.name}</p>
                          <p className="text-xs text-slate-500">
                            {angler.counting.length} fish counting
                            {angler.fish.length > maxFish && ` (${angler.fish.length} entered)`}
                          </p>
                        </div>
                      </div>
                      <p className={`text-2xl font-bold tabular-nums ${medal ? medal.text : "text-slate-300"}`}>
                        {heaviestBag ? formatWeight(angler.total) : `${angler.total.toFixed(1)}%`}
                      </p>
                    </div>

                    {showFishInResults && angler.counting.length > 0 && (
                      <div className="mt-3 flex flex-wrap gap-2">
                        {angler.counting.map((f) => (
                          <span
                            key={f.id}
                            className="rounded-lg border border-white/10 bg-slate-900/60 px-2.5 py-1 text-xs text-slate-300"
                          >
                            {f.species} {heaviestBag ? formatWeight(f.weightDec) : `— ${f.pct.toFixed(1)}%`}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {!hasResults && (
              <p className="mt-4 text-center text-sm text-slate-500">Enter fish weights above to see scores.</p>
            )}
          </div>
        )}

      </div>

      {/* Screenshot modal */}
      {showSnapshot && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
          onClick={() => setShowSnapshot(false)}
        >
          <div
            className="w-full max-w-sm rounded-2xl bg-slate-950 p-8 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Clean leaderboard — screenshot this area */}
            <div className="text-center mb-6">
              <p className="text-xs font-semibold uppercase tracking-[0.3em] text-violet-300 mb-1">RSAA</p>
              <h2 className="text-2xl font-bold text-white">{compName}</h2>
              <p className="mt-1 text-xs text-slate-500">
                Best {maxFish} fish · {heaviestBag ? "Heaviest bag" : TARGETS.find(t => t.value === target)?.label}
                {uniqueSpecies ? " · unique species" : ""}
                {fishCountPriority ? " · fish count priority" : ""}
              </p>
            </div>

            <div className="space-y-2">
              {ranked.filter((a) => a.total > 0).map((angler, idx) => {
                const medal = MEDALS[idx];
                return (
                  <div
                    key={angler.id}
                    className={`rounded-xl px-4 py-3 border ${medal ? medal.bg : "border-white/10 bg-white/5"}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <span className="text-xl w-7 text-center">{medal ? medal.icon : `${idx + 1}.`}</span>
                        <span className={`font-bold text-base ${medal ? medal.text : "text-slate-300"}`}>{angler.name}</span>
                      </div>
                      <span className={`font-bold text-lg tabular-nums ${medal ? medal.text : "text-slate-300"}`}>
                        {heaviestBag ? formatWeight(angler.total) : `${angler.total.toFixed(1)}%`}
                      </span>
                    </div>
                    {showFishInResults && angler.counting.length > 0 && (
                      <div className="mt-2 ml-10 flex flex-wrap gap-1.5">
                        {angler.counting.map((f) => (
                          <span key={f.id} className="rounded-md border border-white/10 bg-slate-900/60 px-2 py-0.5 text-xs text-slate-300">
                            {f.species} {formatWeight(f.weightDec)}{heaviestBag ? "" : ` — ${f.pct.toFixed(1)}%`}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <p className="mt-6 text-center text-xs text-slate-500">
              Take a screenshot of this panel, then tap outside to close.
            </p>
            <button
              type="button"
              onClick={() => setShowSnapshot(false)}
              className="mt-4 w-full rounded-xl border border-white/10 py-2 text-sm text-slate-400 hover:text-white transition"
            >
              Close
            </button>
          </div>
        </div>
      )}

    </div>
  );
}
